<template>
  <div>
    <b-tabs size="lg" horizontal active-nav-item-class="active">
      <b-tab :active="currentTab === 'account'" @click="onTabChange('account')">
        <template #title>
          <span>{{ t('Account Information') }}</span>
        </template>
      </b-tab>
      <b-tab
        v-if="data && data.type === 2"
        :active="currentTab === 'finance'"
        @click="onTabChange('finance')"
      >
        <template #title>
          <span>{{ t('Current Account Information') }}</span>
        </template>
      </b-tab>

      <b-card no-body>
        <b-card-body>
          <customer-detail
            ref="customerDetail"
            type="edit"
            :submitting="submitting"
            :values="data"
            :active-tab="currentTab"
            @update-current-account-code="onUpdateCurrentAccountCode"
            @submit="submit"
          />
        </b-card-body>
      </b-card>
    </b-tabs>
  </div>
</template>

<script>
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { BCard, BCardBody, BTabs, BTab } from 'bootstrap-vue';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
import CustomerDetail from '@/views/pages/customers/CustomerDetail.vue';

export default {
  components: {
    BCard,
    BCardBody,
    BTabs,
    BTab,
    CustomerDetail
  },
  mixins: [helper],
  data() {
    return {
      submitting: false,
      id: '',
      data: {},
      currentTab: 'account'
    };
  },
  created() {
    const { id } = this.$route.params;
    this.id = id;
    this.getCustomerData(id);
  },
  methods: {
    onTabChange(name) {
      this.currentTab = name;
    },
    submit(data) {
      this.submitting = true;
      useService
        .editCustomer(this.id, data)
        .then(response => {
          this.showSuccessMessage(`${response.message}`);
          this.$router.push({ name: 'customers_list' });
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    onUpdateCurrentAccountCode(accountNumber) {
      if (!accountNumber) {
        this.showErrorMessage(this.$t('Please enter a valid account number'));
        return;
      }
      const ledgerAccountId = this.data && this.data.ledgerAccountId;
      if (!ledgerAccountId) {
        this.showErrorMessage(this.$t('Ledger account not found'));
        return;
      }
      this.submitting = true;
      useService
        .updateLedgerAccountNumber(ledgerAccountId, accountNumber)
        .then(response => {
          this.showSuccessMessage(`${response.message || this.$t('Updated Successfully')}`);
          // Update local value so that the button hides
          this.data.current_account_code = accountNumber;
          this.data.current_account_code_initial = accountNumber;
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    getCustomerData(id) {
      this.submitting = true;
      useService
        .getCustomer(id)
        .then(response => {
          const apiData = response.data || {};
          const defaultLedgerAccount = apiData.defaultLedgerAccount || {};
          const legalIdentityInfo = defaultLedgerAccount.legalIdentityInfo || {};
          const legalContactInfo = legalIdentityInfo.contactInfo || {};
          const addressInfo = legalIdentityInfo.addressInfo || {};
          const authorizedContact = defaultLedgerAccount.authorizedContact || {};

          const mappedValues = {
            // Account tab fields
            name: apiData.name || '',
            authorizedName: apiData.authorizedName || '',
            authorizedNo: apiData.authorizedNo || '',
            phone: apiData.phone || authorizedContact.mobilePhoneNumber || '',
            email: apiData.email || legalContactInfo.email || '',
            paymentType: apiData.paymentType ?? '',
            status: apiData.status ?? 1,
            package_id: (apiData.package && apiData.package.id) || '',
            type: apiData.type ?? 2,

            // Finance tab fields
            current_account_code: defaultLedgerAccount.accountNumber || '',
            current_account_code_initial: defaultLedgerAccount.accountNumber || '',
            tax_office: legalIdentityInfo.taxOffice || '',
            tax_no: legalIdentityInfo.taxNumber || '',
            mersis_number: legalIdentityInfo.mersisNumber || '',
            trade_registry_number: legalIdentityInfo.tradeRegistryNumber || '',
            iban: defaultLedgerAccount.bankIban || '',
            company_fixed_phone: legalContactInfo.landlinePhoneNumber || '',
            fixed_phone: authorizedContact.landlinePhoneNumber || '',

            // Company and address info (shown as read-only in finance form)
            address: addressInfo.address || '',
            country: addressInfo.countryIso2
              ? { text: addressInfo.countryIso2, value: '', iso2: addressInfo.countryIso2 }
              : '',
            city: addressInfo.city ? { text: addressInfo.city, value: '' } : '',
            state: addressInfo.state ? { text: addressInfo.state, value: '' } : '',
            postalCode: addressInfo.postalCode
              ? {
                  value: addressInfo.postalCode,
                  combinedLabel: `${addressInfo.postalCode} - ${addressInfo.city || ''}`.trim(),
                  state: addressInfo.state || '',
                  city: addressInfo.city || ''
                }
              : '',
            companyType: legalIdentityInfo.companyType ?? ''
          };

          this.data = {
            ...mappedValues,
            ledgerAccountId: defaultLedgerAccount.id || ''
          };
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            messages.forEach(item => {
              this.showErrorMessage(item.message);
            });
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
